import React, { useRef, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  alpha,
  Button,
  Card,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles"; // Import ThemeProvider and createTheme
import { motion } from "framer-motion";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  UserPlus, // Registration
  Search, // Browsing/Searching
  DollarSign, // Placing bids/RFQs, secure payment
  Handshake, // Negotiation
  Truck, // Logistics coordination, pickup scheduling
  ListPlus, // Creating listings
  Receipt, // Secure payment receipt
  Scale, // Transparent weighing
  ArrowRight, // CTA
  ShieldCheck, // Explicitly imported
  MapPin as MapPinIcon, // Renamed to avoid conflict with MapPin for tracking
} from "lucide-react";
import { FaBolt, FaCheckCircle, FaEye, FaUserEdit } from "react-icons/fa";
import Seo from "../components/Seo";

import Breadcrumb from "../components/Breadcrumb";
import { generateBreadcrumbs } from "../utils/breadcrumbs";

import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

// Swiper imports for v10+
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";


// Define theme colors for consistency and easier modification
const themeColors = {
  primary: "#F5B800", // Original brand color
  secondary: "#334155", // A deep, subtle grey for text
  background: "#f8fafd", // A very light, almost off-white background
  cardBackground: "#ffffff",
  shadow: "rgba(0, 0, 0, 0.08)", // Subtle shadow
  ctaBackground: "#2d3748", // A dark background for the CTA section
  accentLight: alpha("#F5B800", 0.05), // Lighter primary for backgrounds
  accentDark: alpha("#334155", 0.05), // Lighter secondary for backgrounds
};

// Create a custom Material-UI theme
const theme = createTheme({
  typography: {
    fontFamily: "Inter, sans-serif", // Use Inter font
  },
  palette: {
    primary: {
      main: themeColors.primary,
    },
    secondary: {
      main: themeColors.secondary,
    },
    background: {
      default: themeColors.background,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12, // Apply rounded corners to buttons
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12, // Apply rounded corners to cards
        },
      },
    },
  },
});

// Framer Motion Variants (reused from About Us for consistency)
const sectionVariant = {
  hidden: { opacity: 0, y: 50, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: "easeInOut",
      when: "beforeChildren",
    },
  },
};

const itemVariant = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

// Reimagined icon style for main sections (not directly used in this update, but kept for context)
// const iconBoxStyle = {
//   width: 64,
//   height: 64,
//   borderRadius: "50%",
//   display: "flex",
//   alignItems: "center",
//   justifyContent: "center",
//   background: `linear-gradient(45deg, ${themeColors.primary} 30%, ${alpha(
//     themeColors.primary,
//     0.8
//   )} 90%)`,
//   color: "#fff",
//   boxShadow: `0px 10px 30px ${alpha(themeColors.primary, 0.3)}`,
//   flexShrink: 0,
// };

// // Icon style for steps/features (used in Reverse Logistics section)
// const stepIconStyle = {
//   width: 60,
//   height: 60,
//   borderRadius: "50%",
//   backgroundColor: alpha(themeColors.primary, 0.15),
//   boxShadow: `0 6px 18px ${alpha(themeColors.primary, 0.15)}`,
//   display: "flex",
//   alignItems: "center",
//   justifyContent: "center",
//   flexShrink: 0,
// };

// New style for the step number badge (used in Buyer/Seller cards)
const stepNumberBadgeStyle = {
  position: "absolute",
  top: -6, // Adjusted for smaller size
  right: -6, // Adjusted for smaller size
  width: 22, // Smaller number circle
  height: 22,
  borderRadius: "50%",
  backgroundColor: themeColors.secondary, // Different color for number badge
  color: themeColors.cardBackground,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 700,
  fontSize: "0.8rem", // Smaller font size
  boxShadow: `0 2px 6px ${alpha(themeColors.secondary, 0.3)}`,
};

// New style for the step icon container in Buyer/Seller cards
const buyerSellerStepIconStyle = {
  width: 60, // Smaller icon container
  height: 60,
  borderRadius: "50%",
  backgroundColor: alpha(themeColors.primary, 0.1), // Lighter background for the icon circle
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  position: "relative", // For positioning the number badge
};

const HowItWorksPage = () => {
  const buyerSteps = [
    {
      icon: UserPlus,
      title: "Register & Verify",
      desc: "Sign up in minutes, complete verification, and trade.",
    },
    {
      icon: Search,
      title: "Browse Listings",
      desc: "Find materials by type, quantity, and location with filters.",
    },
    {
      icon: DollarSign,
      title: "Place Bids or RFQs",
      desc: "Submit competitive bids or request tailored offers",
    },
    {
      icon: Handshake,
      title: "Negotiate & Confirm",
      desc: "Chat with sellers, agree on terms, and lock in your deal.",
    },
    {
      icon: ShieldCheck,
      title: "Secure Payment",
      desc: "Use escrow for safe and transparent transactions.",
    },
    {
      icon: Truck,
      title: "Arrange Logistics",
      desc: "Schedule pickup or delivery with our tools or your own partner.",
    },
    {
      icon: MapPinIcon,
      title: "Track & Receive",
      desc: "Monitor your shipment in real time and confirm once delivered.",
    },
  ];

  const sellerSteps = [
    {
      icon: UserPlus,
      title: "Register & Verify",
      desc: "Create profile, complete verification, and get in touch with buyers.",
    },
    {
      icon: ListPlus,
      title: "Create Listings",
      desc: "List your scrap with clear details, photos, and pricing preferences.",
    },
    {
      icon: DollarSign,
      title: "Manage Bids",
      desc: "Receive and review offers from buyers for competitive pricing.",
    },
    {
      icon: Handshake,
      title: "Accept & Confirm",
      desc: "Choose the best bid and confirm transaction terms with the buyer.",
    },
    {
      icon: Receipt,
      title: "Secure Payment",
      desc: "Buyer funds escrow, ensuring safe and guaranteed payment.",
    },
    {
      icon: Truck,
      title: "Schedule Pickup",
      desc: "Arrange collection using integrated logistics or your preferred carrier.",
    },
    {
      icon: Scale,
      title: "Transparent Weighing",
      desc: "Ensure fair weighing at pickup, with records shared for both parties.",
    },
  ];

  const sellers = [
    "Clear Demand – Know exactly what buyers want in real time.",
    "Less Waiting – Respond instantly instead of waiting for bids.",
    "Quick Orders – Accepted requests turn directly into confirmed deals.",
    "Fair Opportunities – Every seller sees the same buyer requests.",
  ];

  const buyers = [
    "Control the Process – Buyers set the terms by posting their needs.",
    "Faster Fulfillment – Sellers can confirm quickly and ship sooner.",
    "Transparent Options – Multiple sellers can respond, creating fair competition.",
  ];

  const buyerScrollRef = useRef(null);
  const sellerScrollRef = useRef(null);
  const secondary = alpha(theme.palette.secondary.main || "#6b7280", 0.85);

  useEffect(() => {
    // const scrollSpeed = 1; // Pixels per interval
    const scrollInterval = 30; // Milliseconds per interval

    const autoScroll = (ref) => {
      if (!ref.current) return;
      const { scrollLeft, scrollWidth, } = ref.current;

      // Loop seamlessly through first half only (to avoid white flash)
      const maxScroll = scrollWidth / 2;

      ref.current.scrollLeft = (scrollLeft + 1) % maxScroll;
    };

    // Start auto-scrolling for both sections
    const intervalBuyer = setInterval(
      () => autoScroll(buyerScrollRef, buyerSteps.length),
      scrollInterval
    );
    const intervalSeller = setInterval(
      () => autoScroll(sellerScrollRef, sellerSteps.length),
      scrollInterval
    );

    // Cleanup function to clear intervals when the component unmounts
    return () => {
      clearInterval(intervalBuyer);
      clearInterval(intervalSeller);
    };
  }, [buyerSteps.length, sellerSteps.length]); // Re-run effect if step counts change

  const breadcrumbs = generateBreadcrumbs({ pageType: "howItWorks" });

  const { token, role } = useSelector((state) => state.auth);
  const isLoggedIn = Boolean(token);
  const isBuyerLoggedIn = Boolean(token && role === "buyer");
  const isSellerLoggedIn = Boolean(token && role === "seller");


  return (
    <>
      <Seo
        title="How ScrapTrade Works | Simple Scrap Trading"
        description="Learn how ScrapTrade makes scrap trading easy: verified partners, transparent pricing, secure payments, and logistics built for recyclers."
        url="https://scraptrade.com.au/how-it-works"
        canonical="https://scraptrade.com.au/how-it-works"
        image="https://scraptrade.com.au/assets/og/scrap.jpg"
        breadcrumbs={breadcrumbs}
        extraSchema={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "How do I sell scrap on ScrapTrade?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Register, list your scrap, and connect with verified buyers."
              }
            },
            {
              "@type": "Question",
              name: "Is ScrapTrade safe for transactions?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes, all transactions are secured and verified through our trusted platform."
              }
            }
          ]
        }}
      />

      <ThemeProvider theme={theme}>
        {" "}
        {/* Wrap the component with ThemeProvider */}
        <Box sx={{ backgroundColor: themeColors.background, pt: 0 }}>
          {/* Hero Section - How It Works */}
          <Box
            sx={{
              py: { xs: 5, md: 10 },
              backgroundColor: themeColors.accentLight,
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundImage:
                  "radial-gradient(circle, #ffffff 1px, transparent 1px)",
                backgroundSize: "20px 20px",
                opacity: 0.1,
                zIndex: 0,
              },
            }}
          >
            <Container
              maxWidth="lg"
              sx={{ position: "relative", zIndex: 1, textAlign: "center" }}
            >
              <motion.div
                initial="hidden"
                whileInView="visible"
                variants={sectionVariant}
                viewport={{ once: true, amount: 0.4 }}
              >
                <Typography
                  variant="h2"
                  fontWeight={800}
                  gutterBottom
                  sx={{
                    color: themeColors.secondary,
                    fontSize: { xs: "2.8rem", sm: "3.5rem", md: "4.5rem" },
                    lineHeight: 1.1,
                  }}
                >
                  How{" "}
                  <Box component="span" sx={{ color: themeColors.primary }}>
                    ScrapTrade
                  </Box>{" "}
                  Works
                </Typography>

                <Typography
                  variant="h5"
                  color="text.secondary"
                  paragraph
                  sx={{
                    lineHeight: 1.6,
                    color: alpha(themeColors.secondary, 0.8),
                    maxWidth: "800px",
                    mx: "auto",
                  }}
                >
                  Simplify scrap trading with real-time pricing and secure deals. <br />
                  The smarter, safer way to buy and sell scrap online.
                </Typography>

              </motion.div>
            </Container>
          </Box>

          <Container maxWidth="lg" sx={{ mt: 3, mb: 3, backgroundColor: themeColors.background, pt: 0 }}>
            <Breadcrumb items={breadcrumbs} />
          </Container>

          {/* How It Works: For buyer */}
          <Box
            sx={{
              py: { xs: 5, md: 10 },
              backgroundColor: themeColors.cardBackground,
              "& .swiper-pagination": {
                bottom: "-5px !important", // move dots below content
              },
              "& .swiper-pagination-bullet": {
                width: "10px",
                height: "10px",
                backgroundColor: alpha(themeColors.secondary, 0.3),
                opacity: 1,
                transition: "all 0.3s ease",
              },
              "& .swiper-pagination-bullet-active": {
                backgroundColor: themeColors.primary,
                width: "20px",
                borderRadius: "5px", // pill style active dot
              },
            }}
          >
            <Container maxWidth="lg">
              <motion.div
                initial="hidden"
                whileInView="visible"
                variants={sectionVariant}
                viewport={{ once: true, amount: 0.3 }}
              >
                <Typography
                  variant="h3"
                  fontWeight={700}
                  textAlign="center"
                  gutterBottom
                  sx={{
                    color: themeColors.secondary,
                    fontSize: { xs: "2.2rem", md: "3.2rem" },
                  }}
                >
                  For{" "}
                  <Box component="span" sx={{ color: themeColors.primary }}>
                    Buyers
                  </Box>
                </Typography>
                <Typography
                  variant="h6"
                  textAlign="center"
                  maxWidth="md"
                  mx="auto"
                  mb={6}
                  sx={{
                    lineHeight: 1.6,
                    color: alpha(themeColors.secondary, 0.8),
                  }}
                >
                  Find the quality scrap materials you need with ease and confidence. <br />
                  Buy scrap in just 7 simple steps
                </Typography>
              </motion.div>

              <Swiper
                modules={[Pagination, Autoplay]}
                spaceBetween={20}
                slidesPerView={1}
                slidesPerGroup={1}
                loop={true}
                autoplay={{
                  delay: 1500,
                  disableOnInteraction: false,
                }}
                speed={800}
                pagination={{ clickable: true }}
                breakpoints={{
                  600: { slidesPerView: 2, slidesPerGroup: 1 },
                  900: { slidesPerView: 3, slidesPerGroup: 1 },
                  1200: { slidesPerView: 4, slidesPerGroup: 1 },
                }}
                style={{
                  padding: "30px 20px 50px 20px", // added top and side padding for shadow breathing space
                  margin: "-30px -20px 0 -20px", // offset padding to maintain alignment
                }}
                onSwiper={(swiper) => {
                  // store swiper instance for pause/resume
                  swiper.el.querySelectorAll(".hover-card").forEach((card) => {
                    card.addEventListener("mouseenter", () => swiper.autoplay.stop());
                    card.addEventListener("mouseleave", () => swiper.autoplay.start());
                  });
                }}
              >
                {buyerSteps.map((step, idx) => (
                  <SwiperSlide key={idx}>
                    <motion.div
                      initial="hidden"
                      whileInView="visible"
                      variants={itemVariant}
                      viewport={{ once: true, amount: 0.3 }}
                    >
                      <Card
                        className="hover-card" // 👈 hook for hover
                        sx={{
                          textAlign: "center",
                          p: 2,
                          backgroundColor: themeColors.cardBackground,
                          boxShadow: `0 8px 30px ${alpha(themeColors.shadow, 0.15)}`,
                          borderRadius: 3,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          transition: "transform 0.3s ease, box-shadow 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-6px)",
                            boxShadow: `0 12px 30px ${alpha(themeColors.shadow, 0.25)}`,
                            cursor: "pointer",
                          },
                        }}
                      >
                        <Box sx={buyerSellerStepIconStyle}>
                          <step.icon size={30} color={themeColors.primary} />
                          <Box sx={stepNumberBadgeStyle}>{idx + 1}</Box>
                        </Box>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          gutterBottom
                          sx={{
                            color: themeColors.secondary,
                            mt: 1,
                            fontSize: "1rem",
                          }}
                        >
                          {step.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: alpha(themeColors.secondary, 0.9),
                            fontSize: "0.75rem",
                          }}
                        >
                          {step.desc}
                        </Typography>
                      </Card>
                    </motion.div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <Stack direction="row" justifyContent="center" spacing={2} mt={4}>
                <Button
                  component={Link}
                  to={isBuyerLoggedIn ? "/dashboard" : "/register?role=buyer"}
                  variant="contained"
                  color="primary"
                  sx={{ px: 4, py: 1.5 }}
                >
                  {isBuyerLoggedIn ? "Go to Dashboard" : "Get Started"}
                </Button>
                <Button
                  component={Link}
                  to="/blog"
                  variant="outlined"
                  color="primary"
                  sx={{ px: 4, py: 1.5 }}
                >
                  Watch Demo
                </Button>
              </Stack>
            </Container>
          </Box>

          {/* How It Works: For Sellers */}
          <Box
            sx={{
              py: { xs: 5, md: 10 },
              backgroundColor: themeColors.background,
              "& .swiper-pagination": {
                bottom: "-5px !important",
              },
              "& .swiper-pagination-bullet": {
                width: "10px",
                height: "10px",
                backgroundColor: alpha(themeColors.secondary, 0.3),
                opacity: 1,
                transition: "all 0.3s ease",
              },
              "& .swiper-pagination-bullet-active": {
                backgroundColor: themeColors.primary,
                width: "20px",
                borderRadius: "5px",
              },
            }}
          >
            <Container maxWidth="lg">
              <motion.div
                initial="hidden"
                whileInView="visible"
                variants={sectionVariant}
                viewport={{ once: true, amount: 0.3 }}
              >
                <Typography
                  variant="h3"
                  fontWeight={700}
                  textAlign="center"
                  gutterBottom
                  sx={{
                    color: themeColors.secondary,
                    fontSize: { xs: "2.2rem", md: "3.2rem" },
                  }}
                >
                  For <Box component="span" sx={{ color: themeColors.primary }}>Sellers</Box>
                </Typography>
                <Typography
                  variant="h6"
                  textAlign="center"
                  maxWidth="md"
                  mx="auto"
                  mb={6}
                  sx={{
                    lineHeight: 1.6,
                    color: alpha(themeColors.secondary, 0.8),
                  }}
                >
                  Your Step-by-Step Guide to Selling Scrap Online
                </Typography>
              </motion.div>

              <Swiper
                modules={[Pagination, Autoplay]}
                spaceBetween={20}
                slidesPerView={1}
                slidesPerGroup={1}
                loop={true}
                autoplay={{
                  delay: 1500,
                  disableOnInteraction: false,
                }}
                speed={800}
                pagination={{ clickable: true }}
                breakpoints={{
                  600: { slidesPerView: 2, slidesPerGroup: 1 },
                  900: { slidesPerView: 3, slidesPerGroup: 1 },
                  1200: { slidesPerView: 4, slidesPerGroup: 1 },
                }}
                style={{
                  padding: "30px 20px 50px 20px", // added top and side padding for shadow breathing space
                  margin: "-30px -20px 0 -20px", // offset padding to maintain alignment
                }}
                onSwiper={(swiper) => {
                  swiper.el.querySelectorAll(".hover-card").forEach((card) => {
                    card.addEventListener("mouseenter", () => swiper.autoplay.stop());
                    card.addEventListener("mouseleave", () => swiper.autoplay.start());
                  });
                }}
              >
                {sellerSteps.map((step, idx) => (
                  <SwiperSlide key={idx}>
                    <motion.div
                      initial="hidden"
                      whileInView="visible"
                      variants={itemVariant}
                      viewport={{ once: true, amount: 0.3 }}
                    >
                      <Card
                        className="hover-card"
                        sx={{
                          textAlign: "center",
                          p: 2,
                          backgroundColor: themeColors.cardBackground,
                          boxShadow: `0 8px 30px ${alpha(themeColors.shadow, 0.15)}`,
                          borderRadius: 3,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          transition: "transform 0.3s ease, box-shadow 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-6px) scale(1.05)",
                            boxShadow: `0 12px 35px ${alpha(themeColors.shadow, 0.25)}`,
                            cursor: "pointer",
                            zIndex: 10,
                            position: "relative",
                          },
                        }}
                      >
                        <Box sx={buyerSellerStepIconStyle}>
                          <step.icon size={30} color={themeColors.primary} />
                          <Box sx={stepNumberBadgeStyle}>{idx + 1}</Box>
                        </Box>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          gutterBottom
                          sx={{
                            color: themeColors.secondary,
                            mt: 1,
                            fontSize: "1rem",
                          }}
                        >
                          {step.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: alpha(themeColors.secondary, 0.9),
                            fontSize: "0.75rem",
                          }}
                        >
                          {step.desc}
                        </Typography>
                      </Card>
                    </motion.div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <Stack direction="row" justifyContent="center" spacing={2} mt={4}>
                <Button
                  component={Link}
                  to={isSellerLoggedIn ? "/seller/dashboard" : "/register?role=seller"}
                  variant="contained"
                  color="primary"
                  sx={{ px: 4, py: 1.5 }}
                >
                  {isSellerLoggedIn ? "Go to Seller Dashboard" : "Join as Seller"}
                </Button>
                <Button
                  component={Link}
                  to="/blog"
                  variant="outlined"
                  color="primary"
                  sx={{ px: 4, py: 1.5 }}
                >
                  Watch Demo
                </Button>
              </Stack>
            </Container>
          </Box>


          {/* How It Works: Reverse Buying & Selling */}
          <Box
            sx={{
              py: { xs: 5, md: 10 },
              backgroundColor: themeColors.background,
            }}
          >
            <Container maxWidth="lg">
              {/* Section Title */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                variants={sectionVariant}
                viewport={{ once: true, amount: 0.3 }}
              >
                <Typography
                  variant="h3"
                  fontWeight={700}
                  textAlign="center"
                  gutterBottom
                  sx={{
                    color: themeColors.secondary,
                    fontSize: { xs: "2.4rem", md: "3.4rem" },
                  }}
                >
                  Reverse Bidding  &{" "}
                  <Box component="span" sx={{ color: themeColors.primary }}>
                    Selling
                  </Box>
                </Typography>

                <Typography
                  variant="h6"
                  textAlign="center"
                  maxWidth="md"
                  mx="auto"
                  mb={6}
                  sx={{
                    lineHeight: 1.7,
                    color: alpha(themeColors.secondary, 0.8),
                  }}
                >

                  Reverse bidding flips the process: buyers post what they need, and sellers respond instantly. It’s faster, transparent, and cuts out long negotiations.
                </Typography>
              </motion.div>

              {/* Content Grid */}
              <Grid container spacing={6} alignItems="flex-start">
                {/* Left Content */}
                <Grid item xs={12}>
                  <motion.div
                    initial="hidden"
                    whileInView="visible"
                    variants={itemVariant}
                    viewport={{ once: true, amount: 0.3 }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight={600}
                      gutterBottom
                      sx={{ color: themeColors.secondary }}
                    >
                      What is Reverse Bidding ?
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "1.1rem",
                        lineHeight: 1.8,
                        color: alpha(themeColors.secondary, 0.85),
                        mb: 2,
                      }}
                    >
                      Reverse bidding makes scrap trading faster and easier. Instead of waiting for sellers to list materials, buyers post exactly what they need — like copper, aluminium, or e-waste.
                      All verified sellers see these requests and can respond instantly if they have the material. Once a seller accepts, the request turns into an order, and the deal moves forward without long negotiations.
                      This way, demand comes first, sellers compete to supply, and both sides save time while getting the best value.

                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "1rem",
                        lineHeight: 1.9,
                        color: alpha(themeColors.secondary, 0.75),
                        mb: 4, //  add spacing below intro
                      }}
                    >
                      Instead of sellers listing scrap and waiting for buyers to bid, buyers post what they need —
                      for example, “500kg of copper scrap in Melbourne.”
                    </Typography>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={4} sx={{ mt: 4 }}>
                      {/* Sellers */}
                      <Box sx={{ flex: 1 }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 4,
                            height: "100%",
                            borderRadius: 4,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`, //  Seller border
                            background: alpha(theme.palette.background.paper, 0.85),
                            boxShadow: "0 6px 16px rgba(0,0,0,0.05)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-4px)",
                              boxShadow: "0 12px 24px rgba(0,0,0,0.08)",
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.6)}`, //  stronger on hover
                            },
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 2,
                              fontWeight: 700,
                              color: theme.palette.text.primary,
                            }}
                          >
                            Why Sellers Love It
                          </Typography>
                          <Divider sx={{ mb: 3 }} />
                          <List dense sx={{ py: 0 }}>
                            {sellers.map((point) => (
                              <ListItem key={point} sx={{ alignItems: "flex-start", px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 36, mt: "2px", color: theme.palette.primary.main }}>
                                  <CheckCircleOutlineIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primaryTypographyProps={{
                                    sx: { color: theme.palette.text.secondary, lineHeight: 1.7 },
                                  }}
                                  primary={point}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      </Box>

                      {/* Buyers */}
                      <Box sx={{ flex: 1 }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 4,
                            height: "100%",
                            borderRadius: 4,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`, //  Seller border
                            background: alpha(theme.palette.background.paper, 0.85),
                            boxShadow: "0 6px 16px rgba(0,0,0,0.05)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-4px)",
                              boxShadow: "0 12px 24px rgba(0,0,0,0.08)",
                              border: `1px solid ${alpha(theme.palette.secondary.main, 0.6)}`, //  stronger on hover
                            },
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 2,
                              fontWeight: 700,
                              color: theme.palette.text.primary,
                            }}
                          >
                            Why Buyers Love It
                          </Typography>
                          <Divider sx={{ mb: 3 }} />
                          <List dense sx={{ py: 0 }}>
                            {buyers.map((point) => (
                              <ListItem key={point} sx={{ alignItems: "flex-start", px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 36, mt: "2px", color: theme.palette.primary.main }}>
                                  <CheckCircleOutlineIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primaryTypographyProps={{
                                    sx: { color: theme.palette.text.secondary, lineHeight: 1.7 },
                                  }}
                                  primary={point}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      </Box>
                    </Stack>

                  </motion.div>
                </Grid>

                {/* Right Content (Benefits) */}
                <Grid item xs={12} sx={{ mt: 4 }}>
                  <motion.div
                    initial="hidden"
                    whileInView="visible"
                    variants={itemVariant}
                    viewport={{ once: true, amount: 0.3 }}
                  >
                    <Card
                      sx={{
                        p: 4,
                        boxShadow: `0 12px 40px ${alpha(
                          themeColors.shadow,
                          0.2
                        )}`,
                        borderRadius: 4,
                        backgroundColor: themeColors.background,
                        height: "100%",
                      }}
                    >
                      <Typography
                        variant="h5"
                        fontWeight={600}
                        gutterBottom
                        sx={{ color: themeColors.secondary, mb: 3 }}
                      >
                        Key Benefits
                      </Typography>

                      <Stack direction="row" flexWrap="wrap" spacing={2}>
                        {[
                          {
                            icon: FaUserEdit, // represents buyer control or initiation
                            text: "Buyer-Led Procurement",
                          },
                          {
                            icon: FaBolt, // represents speed
                            text: "Faster Matching & Fulfillment",
                          },
                          {
                            icon: FaEye, // represents visibility
                            text: "Real-Time Demand Visibility",
                          },
                          {
                            icon: FaCheckCircle, // represents success or automation
                            text: "Automated Order Conversion",
                          },
                        ].map((benefit, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              p: 2,
                              flex: "1 1 ",
                              backgroundColor: alpha(themeColors.primary, 0.05),
                              borderRadius: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: alpha(themeColors.primary, 0.1),
                              }}
                            >
                              <benefit.icon
                                size={22}
                                color={themeColors.primary}
                              />
                            </Box>
                            <Typography
                              variant="body1"
                              sx={{
                                color: alpha(themeColors.secondary, 0.9),
                                fontSize: "1rem",
                              }}
                            >
                              {benefit.text}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Card>
                  </motion.div>
                </Grid>
              </Grid>

              {/* Case Study Section */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                variants={sectionVariant}
                viewport={{ once: true, amount: 0.3 }}
              >
                <Card
                  sx={{
                    mt: 10,
                    p: 4,
                    backgroundColor: alpha(themeColors.primary, 0.05),
                    borderLeft: `4px solid ${themeColors.primary}`,
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    textAlign="left"
                    gutterBottom
                    sx={{ color: themeColors.secondary }}
                  >
                    Case Study
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: 1.6,
                      color: alpha(themeColors.secondary, 0.85),
                    }}
                  >
                    A buyer submitted a reverse bid request through ScrapTrade. A
                    seller with matching inventory accepted the request shortly
                    after, triggering the automatic generation of an order. The
                    transaction was completed without delays, demonstrating the
                    speed, efficiency, and reliability of the reverse bidding
                    process.
                  </Typography>
                </Card>
              </motion.div>
            </Container>
          </Box>

          {/* Call to Action Section (reused from About Us) */}
          <Box
            sx={{
              backgroundColor: themeColors.ctaBackground,
              py: { xs: 10, md: 14 },
              textAlign: "center",
            }}
          >
            <Container maxWidth="md">
              <motion.div
                initial="hidden"
                whileInView="visible"
                variants={sectionVariant}
                viewport={{ once: true, amount: 0.4 }}
              >
                <Typography
                  variant="h3"
                  fontWeight={700}
                  gutterBottom
                  sx={{
                    color: themeColors.cardBackground,
                    fontSize: { xs: "2rem", md: "3rem" },
                  }}
                >
                  Ready to{" "}
                  <Box component="span" sx={{ color: themeColors.primary }}>
                    Simplify
                  </Box>{" "}
                  Your Scrap Trade?
                </Typography>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  maxWidth="sm"
                  mx="auto"
                  mb={4}
                  sx={{
                    lineHeight: 1.6,
                    color: alpha(themeColors.cardBackground, 0.8),
                  }}
                >
                  Join ScrapTrade today and experience the future of efficient,
                  transparent, and sustainable scrap material trading.
                </Typography>
                <Button
                  component={Link}
                  to={
                    isBuyerLoggedIn
                      ? "/dashboard"
                      : isSellerLoggedIn
                        ? "/seller/dashboard"
                        : "/register"
                  }
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: themeColors.primary,
                    color: themeColors.cardBackground,
                    fontWeight: 700,
                    py: 1.5,
                    px: 4,
                    borderRadius: 3,
                    boxShadow: `0 8px 24px ${alpha(themeColors.primary, 0.25)}`,
                    "&:hover": {
                      backgroundColor: alpha(themeColors.primary, 0.92),
                      boxShadow: `0 12px 30px ${alpha(themeColors.primary, 0.35)}`,
                      transform: "translateY(-3px)",
                    },
                  }}
                  endIcon={<ArrowRight />}
                >
                  {isLoggedIn ? "Go to Dashboard" : "Get Started Now"}
                </Button>
              </motion.div>
            </Container>
          </Box>
        </Box>
      </ThemeProvider>
    </>
  );
};

export default HowItWorksPage;
